import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL } from "../lib/constants";
import { PageHeader, Loading } from "../components/Layout";
import { UserPlus, KeyRound, RefreshCw, Trash2, Copy, X, Check, CalendarClock, UserMinus, PlayCircle, BookOpen, Send } from "lucide-react";

const ROLES = ["rep", "supervisor", "admin"];
const DOMAIN = "@visolutionz.com";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ROLE_BADGE = {
  admin: "bg-[#1C2530] text-[#F5F3EE]",
  supervisor: "bg-[#3E8F99] text-white",
  rep: "bg-[#EFECE5] text-[#1C2530]",
};

const STATUS_BADGE = {
  active: "bg-[#2F7A3E]/15 text-[#2F7A3E]",
  onboarding: "bg-[#E2711D]/15 text-[#E2711D]",
  offboarding: "bg-[#B23A2E]/15 text-[#B23A2E]",
  departed: "bg-[#94A3AD]/20 text-[#1C2530]/50",
};

function PasswordDialog({ pw, email, userId, onClose }) {
  const [copied, setCopied] = useState(false);
  const [mailState, setMailState] = useState("idle");
  const copy = () => { navigator.clipboard.writeText(pw); setCopied(true); toast.success("Copied"); };
  const emailIt = async () => {
    setMailState("busy");
    try {
      const { data } = await api.post(`/users/${userId}/send-credentials`);
      setMailState("sent");
      toast.success(data.sent ? `Set-password link emailed to ${data.to}` : `Link queued for ${data.to} (emails are suppressed in preview)`);
    } catch (e) { setMailState("idle"); toast.error(e.response?.data?.detail || "Could not email link"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="password-dialog">
      <div className="bg-white border border-[#DFDAD0] shadow-[4px_4px_0px_#DFDAD0] w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Password — shown once</div>
            <h3 className="text-lg font-semibold text-[#1C2530] mt-1">{email}</h3>
          </div>
          <button onClick={onClose} data-testid="password-dialog-close" className="text-[#1C2530]/50 hover:text-[#1C2530]"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-2 border border-[#DFDAD0] bg-[#F5F3EE] p-3">
          <code className="flex-1 font-mono text-sm text-[#1C2530] break-all" data-testid="generated-password">{pw}</code>
          <button onClick={copy} data-testid="copy-password-btn" className="shrink-0 border border-[#DFDAD0] bg-white p-2 hover:bg-[#EFECE5]">
            {copied ? <Check className="w-4 h-4 text-[#3E8F99]" /> : <Copy className="w-4 h-4 text-[#1C2530]" />}
          </button>
        </div>
        <p className="text-xs text-[#1C2530]/60 mt-3">Copy and share this securely, or email the user a one-time set-your-password link (24h, no password in the email). It won't be shown again — regenerate a new one if lost.</p>
        <div className="mt-4 flex gap-2">
          {userId && (
            <button onClick={emailIt} disabled={mailState !== "idle"} aria-busy={mailState === "busy"} data-testid="email-password-btn"
              className="flex-1 flex items-center justify-center gap-2 border border-[#DFDAD0] bg-white py-2.5 text-sm font-medium text-[#1C2530] hover:bg-[#EFECE5] disabled:opacity-60">
              {mailState === "sent" ? <><Check className="w-4 h-4 text-[#3E8F99]" /> Link emailed</> : <><Send className="w-4 h-4" /> {mailState === "busy" ? "Sending…" : "Email set-password link"}</>}
            </button>
          )}
          <button onClick={onClose} data-testid="password-dialog-done" className="flex-1 bg-[#1C2530] text-[#F5F3EE] py-2.5 text-sm font-medium hover:bg-[#2a3644]">Done</button>
        </div>
      </div>
    </div>
  );
}

function RegenConfirmDialog({ email, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="regen-confirm-dialog">
      <div className="bg-white border border-[#DFDAD0] shadow-[4px_4px_0px_#DFDAD0] w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Confirm — regenerate password</div>
            <h3 className="text-lg font-semibold text-[#1C2530] mt-1" data-testid="regen-confirm-email">{email}</h3>
          </div>
          <button onClick={onCancel} data-testid="regen-confirm-close" className="text-[#1C2530]/50 hover:text-[#1C2530]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#1C2530]/80" data-testid="regen-confirm-text">
          Regenerate password for {email}? Their current password stops working immediately and you'll need to share the new one.
        </p>
        <div className="mt-5 flex gap-2">
          <button onClick={onCancel} disabled={busy} data-testid="regen-confirm-cancel" className="flex-1 border border-[#DFDAD0] bg-white py-2.5 text-sm font-medium text-[#1C2530] hover:bg-[#EFECE5]">Cancel</button>
          <button onClick={onConfirm} disabled={busy} aria-busy={busy} data-testid="regen-confirm-submit" className="flex-1 bg-[#1C2530] text-[#F5F3EE] py-2.5 text-sm font-medium hover:bg-[#2a3644] disabled:opacity-60">
            {busy ? "Regenerating…" : "Regenerate password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CoverageModal({ rep, regions, onClose }) {
  const [picked, setPicked] = useState([]);
  const [days, setDays] = useState(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [start, setStart] = useState(9);
  const [end, setEnd] = useState(18);
  const [busy, setBusy] = useState(false);
  const toggle = (arr, v, set) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const submit = async () => {
    if (picked.length === 0 || days.length === 0) { toast.error("Pick at least one region and day"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/coverage/bulk", { rep_id: rep.id, regions: picked, days_of_week: days, start_hour: +start, end_hour: +end });
      toast.success(`Coverage added: ${data.created.join(", ")}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="coverage-modal">
      <div className="bg-white border border-[#DFDAD0] shadow-[4px_4px_0px_#DFDAD0] w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Bulk coverage · multi-region</div>
            <h3 className="text-lg font-semibold text-[#1C2530] mt-1">{rep.name}</h3>
          </div>
          <button onClick={onClose} data-testid="coverage-modal-close" className="text-[#1C2530]/50 hover:text-[#1C2530]"><X className="w-5 h-5" /></button>
        </div>
        <div className="font-mono text-[10px] uppercase text-[#1C2530]/60 mb-1">Regions</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {regions.map((r) => (
            <button key={r.id} onClick={() => toggle(picked, r.name, setPicked)} data-testid={`cov-region-${r.name}`}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase border ${picked.includes(r.name) ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/60 hover:bg-[#EFECE5]"}`}>{r.name}</button>
          ))}
        </div>
        <div className="font-mono text-[10px] uppercase text-[#1C2530]/60 mb-1">Days</div>
        <div className="flex gap-1 mb-4">
          {DAYS.map((d) => (
            <button key={d} onClick={() => toggle(days, d, setDays)}
              className={`w-11 py-1.5 font-mono text-[10px] uppercase border ${days.includes(d) ? "bg-[#3E8F99] text-white border-[#3E8F99]" : "border-[#DFDAD0] text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>{d}</button>
          ))}
        </div>
        <div className="flex items-center gap-3 mb-5">
          <div><label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Start</label><input type="number" min="0" max="23" value={start} onChange={(e) => setStart(e.target.value)} className="w-20 mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530]" /></div>
          <div><label className="font-mono text-[10px] uppercase text-[#1C2530]/60">End</label><input type="number" min="0" max="23" value={end} onChange={(e) => setEnd(e.target.value)} className="w-20 mt-1 border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530]" /></div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="coverage-modal-submit"
          className="w-full bg-[#1C2530] text-[#F5F3EE] py-2.5 text-sm font-medium hover:bg-[#2a3644] disabled:opacity-50">
          {busy ? "Adding…" : `Add ${picked.length || ""} coverage ${picked.length === 1 ? "entry" : "entries"}`}
        </button>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "rep", region: "India", password: "", onboarding: false, start_date: "" });
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [regenTarget, setRegenTarget] = useState(null);
  const [regenBusy, setRegenBusy] = useState(false);
  const [covModal, setCovModal] = useState(null);
  const [pwEdit, setPwEdit] = useState({});
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const PAGE_SIZE = 25;

  const load = (p = page, q = search) => api.get("/users", { params: { skip: p * PAGE_SIZE, limit: PAGE_SIZE, q: q || undefined } })
    .then((r) => { setUsers(r.data); setTotal(parseInt(r.headers["x-total-count"] || r.data.length, 10)); });
  useEffect(() => {
    load(0, search);
    api.get("/regions").then((r) => {
      setRegions(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, region: r.data[0].name }));
    });
  }, []);

  // Debounced search — reset to first page on query change.
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(0, search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const goPage = (p) => { setPage(p); setUsers(null); load(p, search); };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email.toLowerCase().endsWith(DOMAIN)) { toast.error(`Email must be a ${DOMAIN} address`); return; }
    setBusy(true);
    try {
      const regionVal = form.role === "rep" ? form.region : "All";
      const { data } = await api.post("/users", { ...form, region: regionVal, password: form.password || undefined, start_date: form.start_date || undefined });
      if (data.pending_approval) {
        toast.info(data.message || "Submitted for approval");
      } else {
        toast.success(`${data.email} created${data.status === "onboarding" ? " (onboarding stub)" : ""}`);
        if (data.generated_password) setDialog({ pw: data.generated_password, email: data.email, userId: data.id });
      }
      setForm({ email: "", name: "", role: "rep", region: regions[0]?.name || "India", password: "", onboarding: false, start_date: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); } finally { setBusy(false); }
  };

  const setPw = async (u) => {
    const val = pwEdit[u.id];
    if (!val || val.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${u.id}/set-password`, { password: val });
      toast.success(`Password set for ${u.email}`);
      setPwEdit((p) => ({ ...p, [u.id]: "" }));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const sendPack = async (u) => {
    try { const { data } = await api.post(`/users/${u.id}/welcome-pack`); if (!data.sent) toast.info(data.message || "Not sent"); else toast.success(data.method === "tenant_mailbox_pdf" ? `Guide PDF emailed to ${u.email}` : `Guide link emailed to ${u.email}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send guide pack"); }
  };
  const regen = (u) => setRegenTarget(u);
  const confirmRegen = async () => {
    const u = regenTarget;
    setRegenBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/regenerate-password`);
      setRegenTarget(null);
      setDialog({ pw: data.generated_password, email: u.email, userId: u.id });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setRegenBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success(`${u.email} deleted`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const canManage = (u) => !(u.role === "admin" && user.role !== "admin");

  const changeRegion = async (u, region) => {
    try { await api.patch(`/users/${u.id}`, { region }); toast.success(`${u.name} home region → ${region}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const activate = async (u) => {
    try { await api.post(`/users/${u.id}/activate`); toast.success(`${u.name} activated — can now sign in`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const offboard = async (u) => {
    if (!window.confirm(`Start offboarding ${u.name}? Their open leads will need reassignment before you can complete it.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/offboard/start`);
      toast.success(`Offboarding started — ${data.open_leads} open lead(s) queued for reassignment`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <PageHeader eyebrow="ACCESS CONTROL" title="User Management" hint="Create accounts, set roles, reset passwords and manage regions. Roles and their permissions are defined under Organization & Roles." />
      <div className="p-8 space-y-8">
        <form onSubmit={createUser} className="border border-[#DFDAD0] bg-white p-5" data-testid="create-user-form">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">
            <UserPlus className="w-3.5 h-3.5" /> Provision new account · {DOMAIN} only
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={`name${DOMAIN}`} data-testid="new-user-email"
              className="md:col-span-2 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name" data-testid="new-user-name"
              className="border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role"
              className="border border-[#DFDAD0] px-2 py-2 text-sm bg-white outline-none focus:border-[#1C2530]">
              {ROLES.filter((r) => r !== "admin" || user.role === "admin").map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <select value={form.role === "rep" ? form.region : "All"} disabled={form.role !== "rep"}
              onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="new-user-region"
              className="border border-[#DFDAD0] px-2 py-2 text-sm bg-white outline-none focus:border-[#1C2530] disabled:opacity-60">
              {form.role === "rep"
                ? regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)
                : <option value="All">All regions</option>}
            </select>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Optional password — leave blank to auto-generate a strong one" data-testid="new-user-password"
              className="flex-1 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
            <button type="submit" disabled={busy} data-testid="create-user-btn"
              className="bg-[#1C2530] text-[#F5F3EE] px-5 py-2 text-sm font-medium hover:bg-[#2a3644] disabled:opacity-50 whitespace-nowrap">
              {busy ? "Creating…" : "Create user"}
            </button>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#DFDAD0]">
            <label className="flex items-center gap-2 text-sm text-[#1C2530] cursor-pointer" data-testid="onboarding-toggle-label">
              <input type="checkbox" checked={form.onboarding} onChange={(e) => setForm({ ...form, onboarding: e.target.checked })}
                data-testid="new-user-onboarding" className="w-4 h-4 accent-[#E2711D]" />
              Onboarding stub — <span className="text-[#1C2530]/60">provision ahead; blocked from login until activated</span>
            </label>
            {form.onboarding && (
              <div className="flex items-center gap-2">
                <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Start date</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="new-user-start-date" className="border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
              </div>
            )}
          </div>
        </form>

        <div className="flex items-center justify-between gap-3">
          <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="w-72 border border-[#DFDAD0] px-3 py-2 text-sm bg-white outline-none focus:border-[#1C2530]" />
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50" data-testid="user-total-count">
            {total} {total === 1 ? "user" : "users"}
          </div>
        </div>
        {!users ? <Loading /> : users.length === 0 ? (
          <div className="border border-[#DFDAD0] bg-white p-8 text-center font-mono text-xs text-[#1C2530]/50" data-testid="users-empty">No users match “{search}”.</div>
        ) : (
          <div className="border border-[#DFDAD0] bg-white" data-testid="users-table">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[#DFDAD0] bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
              <div className="col-span-3">Email (identifier)</div>
              <div className="col-span-2">Name</div>
              <div className="col-span-1">Role</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-5 text-right">Password & lifecycle</div>
            </div>
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[#DFDAD0] last:border-0 items-center" data-testid={`user-row-${u.email}`}>
                <div className="col-span-3 text-sm text-[#1C2530] break-all">{u.email}{u.id === user.id && <span className="ml-2 font-mono text-[9px] text-[#3E8F99]">YOU</span>}</div>
                <div className="col-span-2 text-sm text-[#1C2530]/80">
                  {u.name}
                  {u.role === "rep" && (
                    <select value={u.region || ""} onChange={(e) => changeRegion(u, e.target.value)} data-testid={`home-region-${u.email}`}
                      className="mt-1 block w-full border border-[#DFDAD0] px-1.5 py-1 text-[11px] font-mono bg-white outline-none focus:border-[#1C2530]">
                      {regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  )}
                </div>
                <div className="col-span-1"><span className={`font-mono text-[9px] uppercase px-2 py-1 ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role] || u.role}</span></div>
                <div className="col-span-1"><span className={`font-mono text-[9px] uppercase px-2 py-1 ${STATUS_BADGE[u.status || "active"]}`} data-testid={`status-badge-${u.email}`}>{u.status || "active"}</span></div>
                <div className="col-span-5 flex items-center justify-end gap-2 flex-wrap">
                  {canManage(u) ? (
                    <>
                      {u.status === "onboarding" ? (
                        <button onClick={() => activate(u)} data-testid={`activate-btn-${u.email}`} title="Activate account"
                          className="flex items-center gap-1 border border-[#2F7A3E] text-[#2F7A3E] px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#2F7A3E]/10"><PlayCircle className="w-3 h-3" /> Activate</button>
                      ) : (
                        <input type="text" value={pwEdit[u.id] || ""} onChange={(e) => setPwEdit((p) => ({ ...p, [u.id]: e.target.value }))}
                          placeholder="New password" data-testid={`set-password-input-${u.email}`}
                          className="w-32 border border-[#DFDAD0] px-2 py-1.5 text-xs outline-none focus:border-[#1C2530]" />
                      )}
                      {u.status !== "onboarding" && (
                        <>
                          <button onClick={() => setPw(u)} data-testid={`set-password-btn-${u.email}`} title="Set password"
                            className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#EFECE5]"><KeyRound className="w-3 h-3" /> Set</button>
                          <button onClick={() => regen(u)} data-testid={`regen-password-btn-${u.email}`} title="Regenerate"
                            className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#EFECE5]"><RefreshCw className="w-3 h-3" /> Regen</button>
                          <button onClick={() => sendPack(u)} data-testid={`welcome-pack-btn-${u.email}`} title={u.welcome_pack_sent_at ? `Guide pack sent ${new Date(u.welcome_pack_sent_at).toLocaleDateString()} — resend` : "Send role guide pack"}
                            className={`flex items-center gap-1 border px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#EFECE5] ${u.welcome_pack_sent_at ? "border-[#2F7A3E]/40 text-[#2F7A3E]" : "border-[#DFDAD0]"}`}><BookOpen className="w-3 h-3" /> Guide</button>
                        </>
                      )}
                      {u.role === "rep" && (
                        <button onClick={() => setCovModal(u)} data-testid={`coverage-btn-${u.email}`} title="Add coverage"
                          className="flex items-center gap-1 border border-[#3E8F99] text-[#3E8F99] px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#3E8F99]/10"><CalendarClock className="w-3 h-3" /> Coverage</button>
                      )}
                      {u.role !== "admin" && u.id !== user.id && !["offboarding", "departed", "onboarding"].includes(u.status) && (
                        <button onClick={() => offboard(u)} data-testid={`offboard-btn-${u.email}`} title="Start offboarding"
                          className="flex items-center gap-1 border border-[#B23A2E]/50 text-[#B23A2E] px-2 py-1.5 font-mono text-[9px] uppercase hover:bg-[#B23A2E]/5"><UserMinus className="w-3 h-3" /> Offboard</button>
                      )}
                      {user.role === "admin" && u.id !== user.id && (
                        <button onClick={() => del(u)} data-testid={`delete-user-btn-${u.email}`} title="Delete"
                          className="border border-[#B23A2E]/40 text-[#B23A2E] px-2 py-1.5 hover:bg-[#B23A2E]/5"><Trash2 className="w-3 h-3" /></button>
                      )}
                    </>
                  ) : (
                    <span className="font-mono text-[9px] text-[#1C2530]/40 uppercase">Admin-managed only</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3" data-testid="user-pager">
            <button onClick={() => goPage(page - 1)} disabled={page <= 0} data-testid="user-page-prev"
              className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5] disabled:opacity-40">Prev</button>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60" data-testid="user-page-indicator">Page {page + 1} of {totalPages}</span>
            <button onClick={() => goPage(page + 1)} disabled={page + 1 >= totalPages} data-testid="user-page-next"
              className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5] disabled:opacity-40">Next</button>
          </div>
        )}

        <p className="font-mono text-[10px] text-[#1C2530]/40">
          Email is each account's canonical identifier (maps 1:1 to Microsoft Entra ID later). Reps have no self-service password change — only Admin or Sales Lead set or regenerate passwords here. Use the region dropdown to change a rep's home region, and Coverage to assign them across multiple regions.
        </p>
      </div>
      {dialog && <PasswordDialog pw={dialog.pw} email={dialog.email} userId={dialog.userId} onClose={() => setDialog(null)} />}
      {regenTarget && <RegenConfirmDialog email={regenTarget.email} busy={regenBusy} onCancel={() => setRegenTarget(null)} onConfirm={confirmRegen} />}
      {covModal && <CoverageModal rep={covModal} regions={regions} onClose={() => setCovModal(null)} />}
    </>
  );
}
